import React from 'react';
import { Box, Paper, Typography, Button, useTheme } from '@mui/material';
import {
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../../context/FeatureFlagContext';

/**
 * FeatureProtectedRoute Component
 *
 * Protects routes that require specific features to be enabled.
 * If the feature is disabled, shows an error message instead of the component.
 */
const FeatureProtectedRoute = ({ children, requiredFeature, featureName }) => {
  const theme = useTheme();
  const { isFeatureEnabled, isLoading } = useFeatureFlags();
  const navigate = useNavigate();

  // Show loading state while features are being fetched
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Check if feature is enabled
  const featureEnabled = isFeatureEnabled(requiredFeature);

  // If feature is disabled, show error message
  if (!featureEnabled) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: 'center',
            borderRadius: '12px',
            border: `0.5px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <LockIcon
            sx={{
              fontSize: 64,
              color: theme.palette.text.secondary,
              mb: 2,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 1.5,
            }}
          >
            Feature Not Available
          </Typography>
          <Typography
            sx={{
              fontSize: '17px',
              color: theme.palette.text.secondary,
              mb: 3,
              lineHeight: 1.5,
            }}
          >
            {featureName || requiredFeature} is not enabled for your tenant.
            <br />
            Please contact your administrator to subscribe to this feature.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/prompt-dashboard')}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontSize: '17px',
              fontWeight: 400,
              textTransform: 'none',
              padding: '8px 20px',
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: 'none',
              },
            }}
          >
            Go to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  // Feature is enabled, render the children
  return children;
};

export default FeatureProtectedRoute;
